/**
 * Permissions - Shared permission definitions for frontend authorization
 * LAYER-IDENTITY-ACCESS
 *
 * These permissions mirror the backend constants/permissions.ts.
 * Keep in sync with graphql-svc/src/constants/permissions.ts
 */

import { Role, hasRole } from './auth-types';

/**
 * Permission definitions mapping permissions to allowed roles.
 * Uses resource:action naming convention.
 */
export const PERMISSIONS = {
  // Transaction permissions
  'transaction:create': [Role.CUSTOMER, Role.ADMIN, Role.SUPER_ADMIN],
  'transaction:read:own': [Role.CUSTOMER],
  'transaction:read:assigned': [Role.CASE_MANAGER],
  'transaction:read:all': [Role.COMPLIANCE, Role.ADMIN, Role.SUPER_ADMIN],
  'transaction:update': [Role.CASE_MANAGER, Role.COMPLIANCE, Role.ADMIN, Role.SUPER_ADMIN],
  'transaction:delete': [Role.SUPER_ADMIN],

  // Document permissions
  'document:upload': [Role.CUSTOMER, Role.CASE_MANAGER],
  'document:review': [Role.CASE_MANAGER, Role.COMPLIANCE],
  'document:approve': [Role.COMPLIANCE, Role.ADMIN, Role.SUPER_ADMIN],
  'document:delete': [Role.ADMIN, Role.SUPER_ADMIN],

  // User management
  'user:invite': [Role.ADMIN, Role.SUPER_ADMIN],
  'user:manage': [Role.ADMIN, Role.SUPER_ADMIN],
  'user:read:all': [Role.ADMIN, Role.SUPER_ADMIN],
  'user:deactivate': [Role.SUPER_ADMIN],

  // Compliance actions
  'compliance:approve': [Role.COMPLIANCE, Role.ADMIN, Role.SUPER_ADMIN],
  'compliance:reject': [Role.COMPLIANCE, Role.ADMIN, Role.SUPER_ADMIN],
  'compliance:return': [Role.COMPLIANCE, Role.ADMIN, Role.SUPER_ADMIN],

  // Admin functions
  'admin:settings': [Role.ADMIN, Role.SUPER_ADMIN],
  'admin:audit:read': [Role.ADMIN, Role.SUPER_ADMIN],
  'admin:reports': [Role.ADMIN, Role.SUPER_ADMIN],

  // Message permissions
  'message:send:customer': [Role.CASE_MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
  'message:send:internal': [Role.CASE_MANAGER, Role.COMPLIANCE, Role.ADMIN, Role.SUPER_ADMIN],
  'message:read:all': [Role.CASE_MANAGER, Role.COMPLIANCE, Role.ADMIN, Role.SUPER_ADMIN],
} as const;

/**
 * Permission type - union of all valid permission strings
 */
export type Permission = keyof typeof PERMISSIONS;

/**
 * Check if a user has a specific permission based on their roles.
 * Uses role hierarchy to determine access.
 *
 * @param userRoles - The user's assigned roles
 * @param permission - The permission to check
 * @returns true if user has the permission through any of their roles
 *
 * @example
 * ```ts
 * const userRoles = [Role.ADMIN];
 * hasPermission(userRoles, 'document:review'); // true (ADMIN inherits CASE_MANAGER)
 * hasPermission(userRoles, 'user:deactivate'); // false (only SUPER_ADMIN)
 * ```
 */
export function hasPermission(userRoles: Role[], permission: Permission): boolean {
  const allowedRoles = PERMISSIONS[permission];
  // Check if user has any role that grants this permission
  // Role hierarchy is handled by checking each user role
  return userRoles.some((userRole) =>
    allowedRoles.some(
      (allowedRole) => userRole === allowedRole || hasRole([userRole], allowedRole)
    )
  );
}

/**
 * Get all permissions for a set of roles
 * @param userRoles - The user's assigned roles
 * @returns Array of permissions the user has
 */
export function getPermissionsForRoles(userRoles: Role[]): Permission[] {
  return (Object.keys(PERMISSIONS) as Permission[]).filter((permission) =>
    hasPermission(userRoles, permission)
  );
}
